import type { Market } from '@/legacy/@types/Market';
import type { MarketCode } from '@/legacy/@types/Token';
import type {
  GoogleSettings,
  MicrosoftClaritySettings,
  OidcSettings,
} from '@/legacy/@types/WaykeSettings';

/**
 * Toggle this to switch localhost between Swedish (false) and Norwegian (true) market
 */
const USE_NORWEGIAN_MARKET_FOR_LOCALHOST = false;

/**
 * Market configuration per hostname
 * Mirrors the Kubernetes Markets config from deploy/overlays/{env}/config.yaml
 */
export type MarketConfig = Omit<Market, 'host'>;

export const MARKET_CONFIG: Record<string, MarketConfig> = {
  // Local development - change externalView to 'operations' to test external-ops locally
  localhost: USE_NORWEGIAN_MARKET_FOR_LOCALHOST
    ? {
        marketCode: 'NO',
        locale: 'nb-NO',
        marketplace: 'drive.no',
        dealer: null,
        externalView: null,
      }
    : {
        marketCode: 'SE',
        locale: 'sv-SE',
        marketplace: 'wayke.se',
        dealer: null,
        externalView: null,
      },
  // Production - Sweden
  'dealer.wayke.se': {
    marketCode: 'SE',
    locale: 'sv-SE',
    marketplace: 'wayke.se',
    dealer: null,
    externalView: null,
  },
  // Production - Norway
  'dealer.drive.no': {
    marketCode: 'NO',
    locale: 'nb-NO',
    marketplace: 'drive.no',
    dealer: null,
    externalView: null,
  },
  // Production - External Operations Sweden
  'iordningstallande.wayke.se': {
    marketCode: 'SE',
    locale: 'sv-SE',
    marketplace: 'wayke.se',
    dealer: 'https://dealer.wayke.se',
    externalView: 'operations',
  },
  // Production - External Operations Norway
  'ettermarked.drive.no': {
    marketCode: 'NO',
    locale: 'nb-NO',
    marketplace: 'drive.no',
    dealer: 'https://dealer.drive.no',
    externalView: 'operations',
  },
  // Test - Sweden
  'test-dealer.wayke.se': {
    marketCode: 'SE',
    locale: 'sv-SE',
    marketplace: 'wayke.se',
    dealer: null,
    externalView: null,
  },
  // Test - Norway
  'test-dealer.drive.no': {
    marketCode: 'NO',
    locale: 'nb-NO',
    marketplace: 'drive.no',
    dealer: null,
    externalView: null,
  },
  // Test - External Operations Sweden
  'test-iordningstallande.wayke.se': {
    marketCode: 'SE',
    locale: 'sv-SE',
    marketplace: 'wayke.se',
    dealer: 'https://test-dealer.wayke.se',
    externalView: 'operations',
  },
  // Test - External Operations Norway
  'test-ettermarked.drive.no': {
    marketCode: 'NO',
    locale: 'nb-NO',
    marketplace: 'drive.no',
    dealer: 'https://test-dealer.drive.no',
    externalView: 'operations',
  },
};

/**
 * Get market configuration for a hostname
 * Works both server-side (middleware) and client-side
 */
export function getMarketConfigForHost(host: string): MarketConfig {
  // Direct match
  if (MARKET_CONFIG[host]) {
    return MARKET_CONFIG[host];
  }

  // Fallback to localhost config for unknown hosts (127.x.x.x, 192.168.x.x, etc.)
  return MARKET_CONFIG.localhost;
}

/**
 * Check if a hostname is configured for external operations view
 * Works both server-side (middleware) and client-side
 */
export function isExternalOperationsHost(host: string): boolean {
  const config = getMarketConfigForHost(host);
  return config.externalView === 'operations';
}

/**
 * Get full market configuration derived from hostname (client-side only)
 */
export function getMarket(): Market | undefined {
  if (typeof window === 'undefined') return undefined;

  const host = window.location.hostname;
  const config = getMarketConfigForHost(host);

  return {
    host,
    ...config,
  };
}

/**
 * Get market code from hostname (client-side only)
 */
export function getMarketCode(): MarketCode {
  if (typeof window === 'undefined') return 'SE';
  const host = window.location.hostname;
  return getMarketConfigForHost(host).marketCode;
}

/**
 * Get OIDC settings (market-dependent)
 */
export function getOidcSettings(): OidcSettings {
  return {
    validIssuers: [
      {
        marketCode: 'SE' as MarketCode,
        issuer: process.env.NEXT_PUBLIC_OIDC_ISSUER_SE || '',
      },
      {
        marketCode: 'NO' as MarketCode,
        issuer: process.env.NEXT_PUBLIC_OIDC_ISSUER_NO || '',
      },
    ],
    clientId: process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || 'dealer-ui',
    responseType: process.env.NEXT_PUBLIC_OIDC_RESPONSE_TYPE || 'code',
    scope: process.env.NEXT_PUBLIC_OIDC_SCOPE || 'openid profile offline_access api-resource',
  };
}

/**
 * Get Google Tag Manager settings (market-dependent)
 */
export function getGoogleSettings(): GoogleSettings | null {
  const marketCode = getMarketCode();
  const gtmId =
    marketCode === 'NO' ? process.env.NEXT_PUBLIC_GTM_ID_NO : process.env.NEXT_PUBLIC_GTM_ID_SE;

  if (!gtmId) return null;

  return {
    marketCode,
    gtm: {
      id: gtmId,
      auth:
        marketCode === 'NO'
          ? process.env.NEXT_PUBLIC_GTM_AUTH_NO
          : process.env.NEXT_PUBLIC_GTM_AUTH_SE,
      preview:
        marketCode === 'NO'
          ? process.env.NEXT_PUBLIC_GTM_PREVIEW_NO
          : process.env.NEXT_PUBLIC_GTM_PREVIEW_SE,
    },
  };
}

/**
 * Get Microsoft Clarity settings (market-dependent)
 */
export function getMicrosoftClarityId(): MicrosoftClaritySettings {
  const marketCode = getMarketCode();
  return {
    marketCode,
    id:
      marketCode === 'NO'
        ? process.env.NEXT_PUBLIC_CLARITY_ID_NO
        : process.env.NEXT_PUBLIC_CLARITY_ID_SE,
  };
}

/**
 * Get favicon theme based on market code
 * SE market uses 'wayke' or 'wayke-test'
 * NO market uses 'drive' or 'drive-test'
 */
export function getFaviconTheme(marketCode: MarketCode): string {
  const isTest = process.env.NEXT_PUBLIC_APP_ENV !== 'production';

  if (marketCode === 'NO') {
    return isTest ? 'drive-test' : 'drive';
  }
  return isTest ? 'wayke-test' : 'wayke';
}

/**
 * Get page title based on market code
 */
export function getPageTitle(marketCode: MarketCode): string {
  return marketCode === 'NO' ? 'Drive Dealer' : 'Wayke Dealer';
}

/**
 * Get theme color based on market code
 */
export function getThemeColor(marketCode: MarketCode): string {
  return marketCode === 'NO' ? '#f6f6f6' : '#f0f0f0';
}
